/*
 * File Path: client/src/components/dashboard/main/DeviceCard.js
 * Description: A reusable component to display a single device card, with special handling for various sensor types.
 * Developed by: M. YOUCEF Yazid (yazid.youcef@gmail.com)
 * Version: 3.2.0
 * CreateDate: 2025-06-17
 * UpdateDate: 2025-06-22
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    FaLightbulb, FaThermometerHalf, FaPlug, FaVideo, FaLock, FaTint,
    FaWind, FaQuestionCircle, FaPowerOff,
    FaDoorOpen, FaFan, FaBroadcastTower, FaThermometerFull,
    FaSmog, FaBolt, FaTemperatureHigh, FaTemperatureLow,
    FaBatteryFull, FaBatteryThreeQuarters, FaBatteryHalf, FaBatteryQuarter, FaBatteryEmpty
} from 'react-icons/fa';
import './DeviceCard.css';

// --- Icon Mapping ---
const deviceIcons = {
    'Light': FaLightbulb, 'RGBLight': FaLightbulb, 'DimmableLight': FaLightbulb, 'Bulb': FaLightbulb, 'light_bulb': FaLightbulb, 'light_bulbs': FaLightbulb, 'Wall_Switch': FaLightbulb,
    'Switch': FaPlug, 'Outlet': FaPlug, 'SmartPlug': FaPlug, 'Relay': FaBolt, 'Power_Outlet': FaPlug,
    'TemperatureSensor': FaThermometerHalf, 'HumiditySensor': FaTint, 'MotionSensor': FaVideo,
    'DoorSensor': FaDoorOpen, 'WaterLeakSensor': FaTint, 'Flood_Detector': FaTint, 'Leak_Detector': FaTint,
    'SmokeSensor': FaSmog, 'GasSensor': FaSmog, 'TempHumSensor': FaThermometerHalf, 'SHT4X_T_H_SENSOR': FaThermometerHalf,
    'Thermostat': FaThermometerFull, 'AirConditioner': FaWind, 'Fan': FaFan, 'Heater': FaTemperatureHigh, 'Cooler': FaTemperatureLow,
    'Camera': FaVideo, 'Lock': FaLock, 'Siren': FaBroadcastTower,
    'default': FaQuestionCircle
};
const DefaultIcon = FaQuestionCircle;
const TOGGLEABLE_DEVICE_TYPES = [
    'Switch', 'Light', 'Outlet', 'SmartPlug', 'Bulb', 'light_bulb', 'Relay', 'LIGHT_BULB', 'IOT_LIGHT_BULBS', 'light_bulbs', 'Wall_Switch', 'Power_Outlet'
];

const DeviceCard = ({ device, canControl, onToggle }) => {
    const { t } = useTranslation();

    const deviceTypeUpper = device.device_type?.toUpperCase();
    const isToggleableType = TOGGLEABLE_DEVICE_TYPES.includes(device.device_type);
    
    const isMultiBulb = deviceTypeUpper === 'LIGHT_BULBS';
    const isLeakDetector = deviceTypeUpper === 'LEAK_DETECTOR' || deviceTypeUpper === 'FLOOD_DETECTOR';
    const isValueDevice = deviceTypeUpper === 'WALL_SWITCH' || deviceTypeUpper === 'POWER_OUTLET' || isLeakDetector;
    const isShtSensor = deviceTypeUpper === 'SHT4X_T_H_SENSOR';

    // --- Status Check with Grace Period for SHT Sensor ---
    let isOnline = device.device_status === 'online';
    if (isShtSensor && !isOnline && device.last_telemetry_at) {
        const lastTelemetryTime = new Date(device.last_telemetry_at).getTime();
        const thirtyMinutesAgo = Date.now() - (30 * 60 * 1000);
        if (lastTelemetryTime >= thirtyMinutesAgo) {
            isOnline = true; // Force the device to appear online during the grace period
        }
    }
    const isClickable = isToggleableType && device.device_enabled && isOnline && canControl;

    // --- State Parsing ---
    const isOn = String(device.device_state).toUpperCase() === 'ON' || String(device.device_state).toUpperCase() === 'TRUE';
    let bulbStates = { bulb1: false, bulb2: false };
    if (isMultiBulb) {
        if (typeof device.device_state === 'string' && device.device_state.includes(',')) {
            const states = device.device_state.split(',');
            bulbStates.bulb1 = states[0]?.trim().toLowerCase() === 'true';
            bulbStates.bulb2 = states[1]?.trim().toLowerCase() === 'true';
        }
    }

    // --- SHT Sensor Data Parsing ---
    let displayValue = null;
    if (isValueDevice) {
        displayValue = device.device_value;
    } else if (isShtSensor) {
        const tempMatch = device.device_value ? device.device_value.match(/Temperature: ([\d.]+)/) : null;
        const humMatch = device.device_value ? device.device_value.match(/Humidity: ([\d.]+)/) : null;
        const temperature = tempMatch ? tempMatch[1] : null;
        const humidity = humMatch ? humMatch[1] : null;

        if (temperature && humidity) {
            displayValue = `${temperature}°C / ${humidity}%`;
        } else if (temperature) {
            displayValue = `${temperature}°C`;
        } else if (humidity) {
            displayValue = `${humidity}%`;
        }
    }
    
    // --- Unified "ON" state check for styling ---
    const isDeviceOn = isMultiBulb ? (bulbStates.bulb1 || bulbStates.bulb2) : isOn;

    const handleBulbClick = (e, bulbIdentifier) => {
        e.stopPropagation();
        if (!isClickable) return;

        const currentBulbState = bulbStates[bulbIdentifier] ? 'ON' : 'OFF';
        const newBulbState = (currentBulbState === 'ON') ? 'OFF' : 'ON';

        onToggle(device, { [bulbIdentifier]: newBulbState });
    };

    // --- Card Classes & Icons ---
    const cardClasses = [
        'device-card',
        !device.device_enabled ? 'disabled' : '',
        !isOnline ? 'offline' : '',
        isMultiBulb ? 'multi-bulb' : '',
        isDeviceOn && !isLeakDetector ? 'device-on' : '', // Apply general 'on' style unless it's a leak detector
        isShtSensor ? 'sht-sensor' : '',
        isLeakDetector && isOn ? 'leak-detected' : '',     // Red for leak
        isLeakDetector && !isOn ? 'leak-safe' : ''          // Green for no leak
    ].filter(Boolean).join(' ');

    const IconComponent = deviceIcons[device.device_type] || DefaultIcon;

    let batteryClass = '';
    let BatteryIcon = null;
    if (device.battery_level !== null && device.battery_level !== undefined) {
        const level = Number(device.battery_level);
        if (level > 85) { BatteryIcon = FaBatteryFull; batteryClass = 'battery-high'; }
        else if (level > 60) { BatteryIcon = FaBatteryThreeQuarters; batteryClass = 'battery-high'; }
        else if (level > 35) { BatteryIcon = FaBatteryHalf; batteryClass = 'battery-medium'; }
        else if (level > 10) { BatteryIcon = FaBatteryQuarter; batteryClass = 'battery-low'; }
        else { BatteryIcon = FaBatteryEmpty; batteryClass = 'battery-low'; }
    }

    return (
        <div className={cardClasses} title={!device.device_enabled ? t('devices.status.disabled') : (!isOnline ? t('devices.status.offline') : '')}>

            {/* Indicators */}
            {!isOnline && device.device_enabled && (<div className="offline-indicator" title={t('devices.status.offline')}><FaPowerOff /></div>)}
            {BatteryIcon && (<div className={`device-battery ${batteryClass}`}><BatteryIcon /> {device.battery_level}%</div>)}

            {/* Main Content Area */}
            {isMultiBulb ? (
                <div className="multi-bulb-container">
                    <div className={`bulb-control ${isClickable ? 'clickable' : ''}`} onClick={(e) => handleBulbClick(e, 'bulb1')}>
                        <FaLightbulb className={`device-icon ${bulbStates.bulb1 ? 'on' : 'off'}`} />
                    </div>
                    <div className={`bulb-control ${isClickable ? 'clickable' : ''}`} onClick={(e) => handleBulbClick(e, 'bulb2')}>
                        <FaLightbulb className={`device-icon ${bulbStates.bulb2 ? 'on' : 'off'}`} />
                    </div>
                </div>
            ) : (
                <div className={`single-device-container ${isClickable ? 'clickable' : ''}`} onClick={() => isClickable && onToggle(device)}>
                    {(isValueDevice || isShtSensor) && displayValue && (<p className="device-value-top">{displayValue}</p>)}
                    <IconComponent className={`device-icon ${isOn && !isShtSensor ? 'on' : 'off'}`} />
                </div>
            )}

            {/* Footer Info */}
            <div className="device-info-footer">
                {device.location && (<p className="device-location">{device.location}</p>)}
                <p className="device-name">{device.device_name}</p>
                {!isValueDevice && !isShtSensor && !isLeakDetector && device.device_value && (<p className="device-value">{device.device_value}</p>)}
            </div>
        </div>
    );
};

export default DeviceCard;
